import { UzInput } from '@utazzo/ui';
import { Loader2, Search } from 'lucide-react';
import React from 'react';

import { apiClient } from '@/lib/api-client';

/**
 * Type-ahead over one of the website's own public lookup routes, for the live
 * landing-page sections: pick a hotel, a stay city or an airport and the form
 * stores its id.
 *
 * It reads the PUBLIC routes (`/stays/cities`, `/flights/airports`) rather than
 * the role-gated `/tbo-hotel/*` ones on purpose. A content editor is not
 * necessarily an ops user, and these are exactly the lists a visitor's search
 * box offers - so whatever an editor can pick here is something the website can
 * actually search for.
 */

export interface LookupOption {
  id: string;
  label: string;
  description?: string;
  /** The raw row, handed back on pick so the caller can store more than the id. */
  raw: Record<string, unknown>;
}

interface Props {
  label: string;
  placeholder?: string;
  hint?: string;
  /** Public API path, e.g. `/stays/cities`. Called with `?q=<term>&limit=…`. */
  path: string;
  /** Turns the response rows into options; return null to drop a row. */
  toOption: (row: Record<string, unknown>) => LookupOption | null;
  onPick: (option: LookupOption) => void;
  disabled?: boolean;
}

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

export const RemoteLookup = ({
  label,
  placeholder,
  hint,
  path,
  toOption,
  onPick,
  disabled,
}: Props): React.JSX.Element => {
  const [term, setTerm] = React.useState('');
  const [options, setOptions] = React.useState<LookupOption[]>([]);
  const [state, setState] = React.useState<'idle' | 'loading' | 'error'>('idle');
  const [open, setOpen] = React.useState(false);
  // `toOption` is usually an inline arrow; keeping it in a ref stops a new
  // identity on every parent render from re-running the search.
  const toOptionRef = React.useRef(toOption);
  React.useEffect(() => {
    toOptionRef.current = toOption;
  });

  React.useEffect(() => {
    const q = term.trim();
    if (q.length < MIN_CHARS) {
      setOptions([]);
      setState('idle');
      return;
    }
    // A slow answer for "du" must not overwrite the one for "dubai".
    let stale = false;
    setState('loading');
    const timer = window.setTimeout(() => {
      apiClient
        .get<unknown>(path, { params: { q, limit: 12 } })
        .then(({ data }) => {
          if (stale) return;
          const rows =
            data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)
              ? ((data as { data: unknown[] }).data as Record<string, unknown>[])
              : [];
          setOptions(
            rows.map((r) => toOptionRef.current(r)).filter((o): o is LookupOption => o !== null),
          );
          setState('idle');
        })
        .catch(() => {
          if (stale) return;
          setOptions([]);
          setState('error');
        });
    }, DEBOUNCE_MS);
    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [term, path]);

  const searched = term.trim().length >= MIN_CHARS;

  return (
    <div className="relative">
      <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-(--fg-faint)" />
        <UzInput
          value={term}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-8"
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          // Delayed so a click on an option lands before the list unmounts.
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        />
        {state === 'loading' && (
          <Loader2 className="absolute top-1/2 right-2.5 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-(--fg-faint)" />
        )}
      </div>
      {hint && <p className="mt-1 text-[12px] text-(--fg-faint)">{hint}</p>}

      {open && searched && state !== 'loading' && (
        <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-(--border) bg-(--bg) py-1 shadow-lg">
          {state === 'error' && (
            <li className="px-3 py-2 text-[12px] text-(--fg-faint)">
              Could not search right now. Try again in a moment.
            </li>
          )}
          {state === 'idle' && options.length === 0 && (
            <li className="px-3 py-2 text-[12px] text-(--fg-faint)">No matches.</li>
          )}
          {options.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                // mousedown, not click: the input's blur would close the list first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(o);
                  setTerm('');
                  setOpen(false);
                }}
                className="block w-full px-3 py-1.5 text-left hover:bg-(--z-50)"
              >
                <span className="block truncate text-sm text-(--fg)">{o.label}</span>
                {o.description && (
                  <span className="block truncate text-[12px] text-(--fg-faint)">
                    {o.description}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
